import {
  memo,
  useEffect,
  useRef,
  type KeyboardEvent,
  type PointerEvent,
} from "react";
import type {
  ControlDefinition,
  InstrumentDefinition,
  PortDefinition,
} from "../domain/instrument";

export type PortPatchGuidance =
  | "source"
  | "compatible"
  | "warning"
  | "invalid"
  | "confirming";

type InstrumentPanelProps = {
  definition: InstrumentDefinition;
  selectedId: string | null;
  controlValues: Record<string, number>;
  onControlChange: (id: string, value: number) => void;
  onInteractionEnd: () => void;
  onInteractionStart: (id: string) => void;
  onSelect: (id: string) => void;
  onPortActivate: (id: string) => void;
  onVisualTargetChange?: (id: string | null) => void;
  portPatchGuidance?: ReadonlyMap<string, PortPatchGuidance>;
  traceActive?: boolean;
  traceNodeIds?: ReadonlySet<string>;
};

type ControlProps = {
  control: ControlDefinition;
  value: number;
  selected: boolean;
  onChange: (value: number) => void;
  onInteractionEnd: () => void;
  onInteractionStart: () => void;
  onSelect: () => void;
};

function clamp(value: number) {
  return Math.min(1, Math.max(0, value));
}

function adjustWithKeyboard(
  event: KeyboardEvent<SVGGElement>,
  value: number,
  defaultValue: number,
  onChange: (value: number) => void,
) {
  const step = event.shiftKey ? 0.01 : 0.05;
  if (event.key === "ArrowUp" || event.key === "ArrowRight") {
    event.preventDefault();
    onChange(clamp(value + step));
  } else if (event.key === "ArrowDown" || event.key === "ArrowLeft") {
    event.preventDefault();
    onChange(clamp(value - step));
  } else if (event.key === "Home") {
    event.preventDefault();
    onChange(defaultValue);
  }
}

function discreteIndex(value: number, count: number) {
  if (count <= 1) return 0;
  return Math.min(count - 1, Math.max(0, Math.round(value * (count - 1))));
}

function discreteValue(index: number, count: number) {
  return count <= 1 ? 0 : index / (count - 1);
}

function adjustDiscreteWithKeyboard(
  event: KeyboardEvent<SVGGElement>,
  index: number,
  count: number,
  onChange: (value: number) => void,
) {
  let nextIndex: number;
  if (event.key === "ArrowUp" || event.key === "ArrowRight") nextIndex = index + 1;
  else if (event.key === "ArrowDown" || event.key === "ArrowLeft") nextIndex = index - 1;
  else if (event.key === "Home") nextIndex = 0;
  else if (event.key === "End") nextIndex = count - 1;
  else return;

  event.preventDefault();
  onChange(discreteValue(Math.min(count - 1, Math.max(0, nextIndex)), count));
}

function usePingPongDiscrete(value: number, count: number) {
  const activeIndex = discreteIndex(value, count);
  const clickState = useRef({
    index: activeIndex,
    direction: (activeIndex >= count - 1 ? -1 : 1) as 1 | -1,
    value,
  });

  useEffect(() => {
    if (clickState.current.value === value) return;
    clickState.current.index = activeIndex;
    clickState.current.value = value;
    if (activeIndex <= 0) clickState.current.direction = 1;
    if (activeIndex >= count - 1) clickState.current.direction = -1;
  }, [activeIndex, count, value]);

  const advance = () => {
    const currentIndex = clickState.current.index;
    const direction =
      currentIndex <= 0 ? 1 : currentIndex >= count - 1 ? -1 : clickState.current.direction;
    const nextIndex = Math.min(count - 1, Math.max(0, currentIndex + direction));
    const nextValue = discreteValue(nextIndex, count);
    clickState.current = {
      index: nextIndex,
      direction: nextIndex >= count - 1 ? -1 : nextIndex <= 0 ? 1 : direction,
      value: nextValue,
    };
    return nextValue;
  };

  return { activeIndex, advance };
}

function ContinuousControl({
  control,
  value,
  selected,
  onChange,
  onInteractionEnd,
  onInteractionStart,
  onSelect,
}: ControlProps) {
  const drag = useRef<{ y: number; value: number } | null>(null);
  const isKnob = control.type === "knob";
  const isWheel = control.type === "wheel";
  const height = isKnob ? control.size + 20 : control.size + (isWheel ? 18 : 22);
  const width = isKnob ? control.size + 20 : isWheel ? 60 : 58;
  const className = isKnob ? "knob-control" : isWheel ? "wheel-control" : "slider-control";

  return (
    <g
      aria-label={control.label}
      aria-orientation={isKnob ? undefined : "vertical"}
      aria-valuemax={100}
      aria-valuemin={0}
      aria-valuenow={Math.round(value * 100)}
      className={`panel-control ${className}${selected ? " is-selected" : ""}`}
      data-panel-id={control.id}
      onDoubleClick={isKnob ? () => onChange(control.defaultValue) : undefined}
      onKeyDown={(event) => adjustWithKeyboard(event, value, control.defaultValue, onChange)}
      onLostPointerCapture={() => {
        if (!drag.current) return;
        drag.current = null;
        onInteractionEnd();
      }}
      onPointerDown={(event: PointerEvent<SVGGElement>) => {
        if (event.button !== 0) return;
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        drag.current = { y: event.clientY, value };
        onInteractionStart();
        onSelect();
      }}
      onPointerMove={(event: PointerEvent<SVGGElement>) => {
        if (!drag.current) return;
        const sensitivity = isKnob ? (event.shiftKey ? 360 : 140) : control.size;
        onChange(clamp(drag.current.value + (drag.current.y - event.clientY) / sensitivity));
      }}
      role="slider"
      tabIndex={0}
      transform={`translate(${control.position.x} ${control.position.y})`}
    >
      <title>{`${control.label}: ${Math.round(value * 100)}%`}</title>
      {isKnob ? (
        <circle className="control-focus" r={control.size / 2 + 10} />
      ) : (
        <rect
          className="control-focus"
          height={height}
          width={width}
          x={-width / 2}
          y={-height / 2}
        />
      )}
    </g>
  );
}

function DiscreteControl({
  control,
  value,
  selected,
  onChange,
  onSelect,
}: ControlProps) {
  const options = control.options ?? [];
  const count = Math.max(1, options.length);
  const { activeIndex, advance } = usePingPongDiscrete(value, count);
  const isLever = control.type === "lever";
  const className = isLever ? "lever-control" : "selector-control";

  const activate = () => {
    onSelect();
    onChange(advance());
  };

  return (
    <g
      aria-label={control.label}
      aria-orientation="horizontal"
      aria-valuemax={count - 1}
      aria-valuemin={0}
      aria-valuenow={activeIndex}
      aria-valuetext={options[activeIndex]?.label}
      className={`panel-control ${className}${selected ? " is-selected" : ""}`}
      data-panel-id={control.id}
      onClick={(event) => {
        event.stopPropagation();
        activate();
      }}
      onKeyDown={(event) => adjustDiscreteWithKeyboard(event, activeIndex, count, onChange)}
      role="slider"
      tabIndex={0}
      transform={`translate(${control.position.x} ${control.position.y})`}
    >
      <title>{`${control.label}: ${options[activeIndex]?.label ?? activeIndex + 1}`}</title>
      {isLever ? (
        <rect className="control-focus" height="66" width="86" x="-43" y="-28" />
      ) : (
        <circle className="control-focus" r={control.size / 2 + 14} />
      )}
    </g>
  );
}

function ButtonControl({
  control,
  value,
  selected,
  onChange,
  onSelect,
}: ControlProps) {
  const active = value >= 0.5;
  const width = control.width ?? control.size;
  const height = control.height ?? control.size;

  const activate = () => {
    onSelect();
    onChange(active ? 0 : 1);
  };

  return (
    <g
      aria-label={`${control.label}: ${active ? "active" : "inactive"}`}
      aria-pressed={active}
      className={`panel-control button-control${control.appearance ? ` is-${control.appearance}` : ""}${active ? " is-active" : ""}${selected ? " is-selected" : ""}`}
      data-panel-id={control.id}
      onClick={(event) => {
        event.stopPropagation();
        activate();
      }}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        activate();
      }}
      role="button"
      tabIndex={0}
      transform={`translate(${control.position.x} ${control.position.y})`}
    >
      <title>{control.label}</title>
      <rect
        className="control-focus"
        height={height + 12}
        rx="6"
        width={width + 12}
        x={-width / 2 - 6}
        y={-height / 2 - 6}
      />
    </g>
  );
}

function SemanticPort({
  port,
  selected,
  traced,
  dimmed,
  onActivate,
  guidance,
}: {
  port: PortDefinition;
  selected: boolean;
  traced: boolean;
  dimmed: boolean;
  onActivate: () => void;
  guidance?: PortPatchGuidance;
}) {
  const size = port.size ?? 13;
  const guidanceLabel =
    guidance === "compatible"
      ? ", compatible endpoint"
      : guidance === "warning" || guidance === "confirming"
        ? ", experimental endpoint, confirmation required"
        : guidance === "invalid"
          ? ", unavailable endpoint"
          : guidance === "source"
            ? ", cable source"
            : "";

  return (
    <g
      aria-disabled={guidance === "invalid"}
      aria-label={`${port.label}, ${port.location ?? "front"} panel, ${port.signalType} ${port.direction}${guidanceLabel}`}
      className={`patch-port port-${port.location ?? "front"} signal-${port.signalType}${selected ? " is-selected" : ""}${traced ? " is-traced" : ""}${dimmed ? " is-trace-dimmed" : ""}${guidance ? ` is-patch-${guidance}` : ""}`}
      data-panel-id={port.id}
      onClick={(event) => {
        event.stopPropagation();
        onActivate();
      }}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        onActivate();
      }}
      role="button"
      tabIndex={0}
      transform={`translate(${port.position.x} ${port.position.y})`}
    >
      <title>{`${port.label} - ${port.signalType} ${port.direction}`}</title>
      <circle className="port-hit" r={Math.max(20, size + 7)} />
    </g>
  );
}

function InstrumentPanelComponent({
  definition,
  selectedId,
  controlValues,
  onControlChange,
  onInteractionEnd,
  onInteractionStart,
  onSelect,
  onPortActivate,
  onVisualTargetChange,
  traceActive = false,
  portPatchGuidance = new Map<string, PortPatchGuidance>(),
  traceNodeIds = new Set<string>(),
}: InstrumentPanelProps) {
  const sectionToneById = new Map(definition.sections.map((section) => [section.id, section.tone]));

  return (
    <g
      aria-label={`${definition.displayName} controls`}
      className="instrument-panel instrument-interaction-layer"
      onBlurCapture={(event) => {
        const target =
          event.relatedTarget instanceof Element
            ? event.relatedTarget.closest<SVGGElement>("[data-panel-id]")
            : null;
        onVisualTargetChange?.(target?.dataset.panelId ?? null);
      }}
      onFocusCapture={(event) => {
        const target =
          event.target instanceof Element
            ? event.target.closest<SVGGElement>("[data-panel-id]")
            : null;
        onVisualTargetChange?.(target?.dataset.panelId ?? null);
      }}
      onPointerLeave={() => onVisualTargetChange?.(null)}
      onPointerOut={(event) => {
        const target =
          event.relatedTarget instanceof Element
            ? event.relatedTarget.closest<SVGGElement>("[data-panel-id]")
            : null;
        onVisualTargetChange?.(target?.dataset.panelId ?? null);
      }}
      onPointerOver={(event) => {
        const target =
          event.target instanceof Element
            ? event.target.closest<SVGGElement>("[data-panel-id]")
            : null;
        onVisualTargetChange?.(target?.dataset.panelId ?? null);
      }}
      role="group"
    >
      {definition.controls.map((control) => {
        const value = controlValues[control.id] ?? control.defaultValue;
        const props: ControlProps = {
          control,
          value,
          selected: selectedId === control.id,
          onChange: (nextValue) => onControlChange(control.id, nextValue),
          onInteractionEnd,
          onInteractionStart: () => onInteractionStart(control.id),
          onSelect: () => onSelect(control.id),
        };

        return (
          <g
            className={`instrument-control-slot tone-${sectionToneById.get(control.sectionId) ?? "dark"} section-${control.sectionId}`}
            key={control.id}
          >
            {control.type === "knob" || control.type === "slider" || control.type === "wheel" ? (
              <ContinuousControl {...props} />
            ) : control.type === "rotary-selector" || control.type === "lever" ? (
              <DiscreteControl {...props} />
            ) : (
              <ButtonControl {...props} />
            )}
          </g>
        );
      })}

      {definition.ports.map((port) => (
        <g
          className={`instrument-port-slot tone-${sectionToneById.get(port.sectionId) ?? "dark"} section-${port.sectionId}`}
          key={port.id}
        >
          <SemanticPort
            dimmed={traceActive && !traceNodeIds.has(port.id)}
            guidance={portPatchGuidance.get(port.id)}
            onActivate={() => onPortActivate(port.id)}
            port={port}
            selected={selectedId === port.id}
            traced={traceNodeIds.has(port.id)}
          />
        </g>
      ))}
    </g>
  );
}

export const InstrumentPanel = memo(InstrumentPanelComponent);
